import React, { useEffect, useState } from 'react';
import { PageContainer } from '../components/Container/PageContainer';
import { Title, Table, Text, Flex, Badge, Select, TextInput, Button, Group, Modal, ScrollArea, Code } from '@mantine/core';
import { dbService } from '../firebaseInstance';
import moment from 'moment';

const AdminTaskLogs = () => {
	const [taskLogs, setTaskLogs] = useState([]);
	const [loading, setLoading] = useState(true);
	const [sortConfig, setSortConfig] = useState({
		key: "createdAt",
		order: "desc",
	});
	const [filterStatus, setFilterStatus] = useState('all');
	const [filterTaskType, setFilterTaskType] = useState('all');
	const [searchUserId, setSearchUserId] = useState('');
	const [selectedLog, setSelectedLog] = useState(null);
	const [modalOpened, setModalOpened] = useState(false);

	// 한국어 날짜 형식 파싱 함수
	const parseKoreanDate = (dateValue) => {
		if (!dateValue) return null;
		
		// Firestore Timestamp 처리
		if (dateValue && typeof dateValue === 'object') {
			// Firestore Timestamp 객체인 경우
			if (dateValue.toDate && typeof dateValue.toDate === 'function') {
				return dateValue.toDate();
			}
			// seconds와 nanoseconds가 있는 객체인 경우
			if (dateValue.seconds !== undefined) {
				return new Date(dateValue.seconds * 1000);
			}
			// 이미 Date 객체인 경우
			if (dateValue instanceof Date) {
				return dateValue;
			}
		}
		
		// 문자열인 경우에만 한국어 날짜 형식 파싱 시도
		if (typeof dateValue === 'string') {
			// "2025년 8월 13일 오전 1시 40분 20초 UTC+9" 형식 파싱
			const koreanDateRegex = /(\d{4})년\s+(\d{1,2})월\s+(\d{1,2})일\s+(오전|오후)\s+(\d{1,2})시\s+(\d{1,2})분\s+(\d{1,2})초/;
			const match = dateValue.match(koreanDateRegex);
			
			if (match) {
				const [, year, month, day, ampm, hour, minute, second] = match;
				let adjustedHour = parseInt(hour);
				
				// 오전/오후 처리
				if (ampm === '오후' && adjustedHour !== 12) {
					adjustedHour += 12;
				} else if (ampm === '오전' && adjustedHour === 12) {
					adjustedHour = 0;
				}
				
				// Date 객체 생성 (월은 0부터 시작하므로 -1)
				return new Date(
					parseInt(year),
					parseInt(month) - 1,
					parseInt(day),
					adjustedHour,
					parseInt(minute),
					parseInt(second)
				);
			}
			
			// 기본 Date 파싱 시도
			return new Date(dateValue);
		}
		
		// 숫자인 경우 (timestamp milliseconds)
		if (typeof dateValue === 'number') {
			return new Date(dateValue);
		}
		
		return null;
	};

	// 정렬 함수
	const applySort = (list, cfg = sortConfig) => {
		const { key, order } = cfg;
		const sortedLogs = [...list].sort((a, b) => {
			let aValue;
			let bValue;

			if (key === 'createdAt' || key === 'executedAt' || key === 'completedAt') {
				aValue = a[key] ? parseKoreanDate(a[key]) : null;
				bValue = b[key] ? parseKoreanDate(b[key]) : null;
			} else if (key === 'duration') {
				// 실행 시간 계산 (완료시간 - 실행시간)
				const aDuration = a.completedAt && a.executedAt 
					? parseKoreanDate(a.completedAt) - parseKoreanDate(a.executedAt)
					: null;
				const bDuration = b.completedAt && b.executedAt
					? parseKoreanDate(b.completedAt) - parseKoreanDate(b.executedAt)
					: null;
				aValue = aDuration;
				bValue = bDuration;
			} else {
				aValue = a[key];
				bValue = b[key];
			}

			if (aValue === null || aValue === undefined) return 1;
			if (bValue === null || bValue === undefined) return -1;

			if (order === 'asc') {
				return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
			}
			return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
		});
		return sortedLogs;
	};

	// 필터링 함수
	const applyFilters = (list) => {
		let filtered = [...list];

		// 상태 필터
		if (filterStatus !== 'all') {
			filtered = filtered.filter(log => log.status === filterStatus);
		}

		// 작업 유형 필터
		if (filterTaskType !== 'all') {
			filtered = filtered.filter(log => log.taskType === filterTaskType);
		}

		// 사용자 ID 검색
		if (searchUserId) {
			filtered = filtered.filter(log => 
				log.userId?.toLowerCase().includes(searchUserId.toLowerCase())
			);
		}

		return filtered;
	};

	useEffect(() => {
		// task_logs 컬렉션 실시간 구독
		const unsubscribe = dbService
			.collection('task_logs')
			.orderBy('createdAt', 'desc')
			.limit(500) // 최근 500개만 가져오기
			.onSnapshot((snapshot) => {
				const logs = [];
				snapshot.forEach((doc) => {
					logs.push({
						id: doc.id,
						...doc.data()
					});
				});
				setTaskLogs(logs);
				setLoading(false);
			}, (error) => {
				console.error('Error fetching task logs:', error);
				setLoading(false);
			});

		return () => unsubscribe();
	}, []);

	const sortLogs = (key) => {
		const order = sortConfig.key === key && sortConfig.order === 'asc' ? 'desc' : 'asc';
		const cfg = { key, order };
		setSortConfig(cfg);
	};

	// 상태에 따른 뱃지 색상
	const getStatusColor = (status) => {
		switch (status) {
			case 'pending': return 'yellow';
			case 'running': return 'blue';
			case 'completed': return 'green';
			case 'failed': return 'red';
			case 'cancelled': return 'gray';
			default: return 'gray';
		}
	};

	// 작업 유형에 따른 뱃지 색상
	const getTaskTypeColor = (taskType) => {
		switch (taskType) {
			case 'lifetime_threads_fetch': return 'indigo';
			case 'insights_fetch': return 'violet';
			case 'media_fetch': return 'cyan';
			case 'interaction_fetch': return 'teal';
			case 'scheduled_post': return 'orange';
			default: return 'gray';
		}
	};

	// 실행 시간 포맷팅
	const formatDuration = (executedAt, completedAt) => {
		if (!executedAt || !completedAt) return '-';
		const duration = parseKoreanDate(completedAt) - parseKoreanDate(executedAt);
		const seconds = Math.floor(duration / 1000);
		const minutes = Math.floor(seconds / 60);
		const hours = Math.floor(minutes / 60);
		
		if (hours > 0) {
			return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
		} else if (minutes > 0) {
			return `${minutes}m ${seconds % 60}s`;
		} else {
			return `${seconds}s`;
		}
	};
	
	// 날짜 표시 포맷팅
	const formatDateDisplay = (dateValue) => {
		if (!dateValue) return '-';
		const date = parseKoreanDate(dateValue);
		if (!date || isNaN(date.getTime())) {
			// 파싱 실패시 원본이 문자열이면 반환, 아니면 '-' 반환
			return typeof dateValue === 'string' ? dateValue : '-';
		}
		return moment(date).format("MM/DD HH:mm:ss");
	};

	// 필터링 및 정렬된 데이터
	const displayLogs = applySort(applyFilters(taskLogs), sortConfig);

	// 고유한 작업 유형 추출
	const uniqueTaskTypes = [...new Set(taskLogs.map(log => log.taskType).filter(Boolean))];

	// 로그 클릭 핸들러
	const handleLogClick = (log) => {
		setSelectedLog(log);
		setModalOpened(true);
	};

	return (
		<PageContainer w="100%">
			<Title order={1} mb="md">Task Logs</Title>
			
			{/* JSON 상세 보기 모달 */}
			<Modal
				opened={modalOpened}
				onClose={() => setModalOpened(false)}
				title={`Task Log: ${selectedLog?.id}`}
				size="xl"
			>
				<ScrollArea h={600}>
					<Code block>
						{selectedLog && JSON.stringify(selectedLog, null, 2)}
					</Code>
				</ScrollArea>
			</Modal>
			
			{/* 필터 섹션 */}
			<Group mb="lg" grow>
				<Select
					label="Status Filter"
					placeholder="All status"
					value={filterStatus}
					onChange={setFilterStatus}
					data={[
						{ value: 'all', label: 'All Status' },
						{ value: 'pending', label: 'Pending' },
						{ value: 'running', label: 'Running' },
						{ value: 'completed', label: 'Completed' },
						{ value: 'failed', label: 'Failed' },
						{ value: 'cancelled', label: 'Cancelled' },
					]}
				/>
				
				<Select
					label="Task Type Filter"
					placeholder="All types"
					value={filterTaskType}
					onChange={setFilterTaskType}
					data={[
						{ value: 'all', label: 'All Types' },
						...uniqueTaskTypes.map(type => ({
							value: type,
							label: type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
						}))
					]}
				/>
				
				<TextInput
					label="Search User ID"
					placeholder="Enter user ID..."
					value={searchUserId}
					onChange={(e) => setSearchUserId(e.currentTarget.value)}
				/>
				
				<Button
					variant="light"
					onClick={() => {
						setFilterStatus('all');
						setFilterTaskType('all');
						setSearchUserId('');
					}}
				>
					Clear Filters
				</Button>
			</Group>

			<Title order={3} fw={300} mb="md">
				Total Logs: <span style={{ fontWeight: "bold" }}>{displayLogs.length}</span>
				{(filterStatus !== 'all' || filterTaskType !== 'all' || searchUserId) && 
					` (Filtered from ${taskLogs.length})`
				}
			</Title>

			{loading ? (
				<Text>Loading task logs...</Text>
			) : (
				<div
					style={{
						position: "relative",
						overflowY: "auto",
						maxHeight: "800px",
						width: "100%",
					}}
				>
					<Table highlightOnHover withBorder withColumnBorders w="100%">
						<Table.Thead
							style={{
								position: "sticky",
								top: 0,
								backgroundColor: "white",
								zIndex: 1,
							}}
						>
							<Table.Tr>
								<Table.Th>Task ID</Table.Th>
								<Table.Th>User ID</Table.Th>
								<Table.Th
									onClick={() => sortLogs("taskType")}
									style={{ cursor: "pointer" }}
								>
									Task Type{" "}
									{sortConfig.key === "taskType"
										? sortConfig.order === "asc"
											? "↑"
											: "↓"
										: ""}
								</Table.Th>
								<Table.Th
									onClick={() => sortLogs("status")}
									style={{ cursor: "pointer" }}
								>
									Status{" "}
									{sortConfig.key === "status"
										? sortConfig.order === "asc"
											? "↑"
											: "↓"
										: ""}
								</Table.Th>
								<Table.Th
									onClick={() => sortLogs("createdAt")}
									style={{ cursor: "pointer" }}
								>
									Created At{" "}
									{sortConfig.key === "createdAt"
										? sortConfig.order === "asc"
											? "↑"
											: "↓"
										: ""}
								</Table.Th>
								<Table.Th
									onClick={() => sortLogs("executedAt")}
									style={{ cursor: "pointer" }}
								>
									Executed At{" "}
									{sortConfig.key === "executedAt"
										? sortConfig.order === "asc"
											? "↑"
											: "↓"
										: ""}
								</Table.Th>
								<Table.Th
									onClick={() => sortLogs("duration")}
									style={{ cursor: "pointer" }}
								>
									Duration{" "}
									{sortConfig.key === "duration"
										? sortConfig.order === "asc"
											? "↑"
											: "↓"
										: ""}
								</Table.Th>
								<Table.Th>Error</Table.Th>
								<Table.Th>Retry Count</Table.Th>
							</Table.Tr>
						</Table.Thead>
						<Table.Tbody>
							{displayLogs.map((log) => (
								<Table.Tr 
									key={log.id}
									onClick={() => handleLogClick(log)}
									style={{ cursor: 'pointer' }}
								>
									<Table.Td>
										<Text fz={12} c="dimmed">
											{log.id.substring(0, 8)}...
										</Text>
									</Table.Td>
									<Table.Td>
										<Text fz={14}>
											{log.userId || '-'}
										</Text>
									</Table.Td>
									<Table.Td>
										<Badge color={getTaskTypeColor(log.taskType)} size="sm">
											{log.taskType?.replace(/_/g, ' ') || '-'}
										</Badge>
									</Table.Td>
									<Table.Td>
										<Badge color={getStatusColor(log.status)} size="sm">
											{log.status || '-'}
										</Badge>
									</Table.Td>
									<Table.Td>
										<Text fz={13}>
											{formatDateDisplay(log.createdAt)}
										</Text>
									</Table.Td>
									<Table.Td>
										<Text fz={13}>
											{formatDateDisplay(log.executedAt)}
										</Text>
									</Table.Td>
									<Table.Td>
										<Text fz={13}>
											{formatDuration(log.executedAt, log.completedAt)}
										</Text>
									</Table.Td>
									<Table.Td>
										<Text fz={12} c={log.error ? "red" : "dimmed"} lineClamp={2}>
											{log.error || '-'}
										</Text>
									</Table.Td>
									<Table.Td>
										<Text fz={14}>
											{log.retryCount || 0}
										</Text>
									</Table.Td>
								</Table.Tr>
							))}
						</Table.Tbody>
					</Table>
				</div>
			)}
		</PageContainer>
	);
};

export default AdminTaskLogs;